import { useEffect, useRef, useCallback, useState } from 'react';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

export function useWebSocket() {
  const wsRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const handlersRef = useRef({});
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);

  const connect = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token || wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(`${WS_URL}/ws?token=${token}`);

      ws.onopen = () => {
        setConnected(true);
        // Start ping interval
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === 'presence') {
            setViewers(msg.viewers || []);
          } else if (msg.type === 'contact_updated') {
            setLastUpdate(msg);
            if (handlersRef.current.onContactUpdated) {
              handlersRef.current.onContactUpdated(msg);
            }
          } else if (msg.type === 'activity_updated') {
            if (handlersRef.current.onActivityUpdated) {
              handlersRef.current.onActivityUpdated(msg);
            }
          }
        } catch {}
      };

      ws.onclose = () => {
        setConnected(false);
        clearInterval(pingTimer.current);
        // Reconnect after 3 seconds
        reconnectTimer.current = setTimeout(() => connect(), 3000);
      };

      ws.onerror = () => {
        ws.close();
      };

      wsRef.current = ws;
    } catch {}
  }, []);

  const disconnect = useCallback(() => {
    clearTimeout(reconnectTimer.current);
    clearInterval(pingTimer.current);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const setViewing = useCallback((contactId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'viewing', contact_id: contactId }));
    }
  }, []);

  const onContactUpdated = useCallback((handler) => {
    handlersRef.current.onContactUpdated = handler;
  }, []);

  const onActivityUpdated = useCallback((handler) => {
    handlersRef.current.onActivityUpdated = handler;
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  return {
    connected,
    viewers,
    lastUpdate,
    setViewing,
    onContactUpdated,
    onActivityUpdated,
    reconnect: connect,
  };
}
